'use client';

import { useEffect } from 'react';
import {
  chunkQueryOf,
  chunkUrlsToWarm,
  TAB_CHUNKS_PATH,
  warmSuffixesFor,
  type TabChunkMap,
} from '@/app/[locale]/event/[eventId]/libs/tabChunks';

// Per page load: the map is fetched at most once and each chunk is warmed at most once.
let mapRequest: Promise<TabChunkMap | null> | null = null;
const warmed = new Set<string>();

/** Under the page's own deployment query, so an old page never warms a newer build's chunks. */
function loadMap(query: string): Promise<TabChunkMap | null> {
  mapRequest ??= fetch(`${TAB_CHUNKS_PATH}${query}`)
    .then((res) => (res.ok ? (res.json() as Promise<TabChunkMap>) : null))
    .catch(() => null);
  return mapRequest;
}

type NetworkInformationLike = { saveData?: boolean; effectiveType?: string };

/** Respect a viewer who asked to save data, and do not compete for a 2G link. */
function shouldSkip(): boolean {
  const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  return connection?.saveData === true || /2g$/.test(connection?.effectiveType ?? '');
}

/** Run once the page has loaded and the browser is idle; returns a canceller. */
function whenIdle(run: () => void): () => void {
  let idleId: number | undefined;
  let timer: number | undefined;
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      timer = window.setTimeout(run, 1500); // Safari has no requestIdleCallback
    }
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
  return () => {
    window.removeEventListener('load', schedule);
    if (idleId !== undefined) window.cancelIdleCallback?.(idleId);
    if (timer !== undefined) window.clearTimeout(timer);
  };
}

/** Whether this browser has the low-priority `<link rel=prefetch>` hint (Chrome, Firefox). */
function canPrefetch(): boolean {
  return document.createElement('link').relList?.supports?.('prefetch') === true;
}

/**
 * Download into the browser cache, low priority, so the router's later `<script>` load is a hit.
 * Safari/iOS lacks `prefetch` and gets no warm-up: `preload` is high priority and would compete
 * with the page on a phone, and a `fetch()` is not reused by the script load (measured in WebKit).
 */
function warm(url: string) {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.as = 'script';
  link.href = url;
  document.head.appendChild(link);
}

/**
 * Warm the JS chunks of the shell's tabs while the viewer reads, so a first click on a tab
 * waits only for the server's answer, not for a chunk download after it. Static files only: no
 * RSC request and no function runs (see `tabChunks.ts`).
 */
export function useWarmTabChunks(tabSuffixes: string[]) {
  const key = JSON.stringify(warmSuffixesFor(tabSuffixes));
  useEffect(() => {
    if (shouldSkip() || !canPrefetch()) return;
    const suffixes = JSON.parse(key) as string[];
    let cancelled = false;
    const cancelIdle = whenIdle(() => {
      const loaded = Array.from(document.scripts, (s) => s.src);
      void loadMap(chunkQueryOf(loaded)).then((map) => {
        if (cancelled || !map) return;
        for (const url of chunkUrlsToWarm(map, suffixes, loaded)) {
          if (warmed.has(url)) continue;
          warmed.add(url);
          warm(url);
        }
      });
    });
    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [key]);
}
